import type { CSSProperties } from 'react';
import { stripAndRuntimeProps } from '../base/storePropsUtils';

function kebabToCamel(prop: string): string {
  return prop.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * 将 inline style 中的 kebab-case 键转为 React camelCase（如 border-radius → borderRadius）
 */
export function normalizeReactStyle(
  style: CSSProperties | Record<string, unknown> | undefined | null,
): CSSProperties | undefined {
  if (!isPlainObject(style)) return undefined;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(style)) {
    if (value === undefined || value === null) continue;
    const camelKey = key.includes('-') ? kebabToCamel(key) : key;
    if (isPlainObject(value)) {
      out[camelKey] = normalizeReactStyle(value);
    } else {
      out[camelKey] = value;
    }
  }
  return Object.keys(out).length > 0 ? (out as CSSProperties) : undefined;
}

/**
 * antd 组件 styles 槽位（root / popup 等）递归归一化
 */
export function normalizeReactStyles(
  styles: Record<string, unknown> | undefined | null,
): Record<string, CSSProperties> | undefined {
  if (!isPlainObject(styles)) return undefined;
  const out: Record<string, CSSProperties> = {};
  for (const [slot, value] of Object.entries(styles)) {
    const normalized = normalizeReactStyle(value as Record<string, unknown>);
    if (normalized) out[slot] = normalized;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * 剔除运行时自定义 prop，并归一化 style / styles 后供 antd DOM 使用
 */
export function prepareAntdDomProps(
  props: Record<string, unknown>,
  extraSkip?: readonly string[],
): Record<string, unknown> {
  const stripped = stripAndRuntimeProps(props, extraSkip) as Record<string, unknown>;
  const normalizedStyles = normalizeReactStyles(stripped.styles as Record<string, unknown> | undefined);
  const normalizedStyle = normalizeReactStyle(stripped.style as Record<string, unknown> | undefined);
  const { styles: _styles, style: _style, ...rest } = stripped;
  return {
    ...rest,
    ...(normalizedStyles ? { styles: normalizedStyles } : {}),
    ...(normalizedStyle ? { style: normalizedStyle } : {}),
  };
}
